import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useAulasByCentro,
  useCreateAula,
  useDeleteAula,
  useToggleAula,
  useUpdateAula,
} from '../../../../api/aulas';
import { AulaCapacityChip } from '../../../../components/aulas/AulaCapacityChip';
import { AulaForm, AulaFormData } from '../../../../components/aulas/AulaForm';
import { BloqueosDrawer } from '../../../../components/aulas/BloqueosDrawer';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Modal,
  CssBaseline,
  ThemeProvider,
  Switch,
  FormControlLabel,
  Chip, // ← NUEVO
} from '@mui/material';
import theme from '../../../../styles/theme';
import { useCentros } from '../../../../hooks/useCentros';

export default function AulasCentroPage() {
  const params = useParams<{ id: string }>();
  const centroId = useMemo(() => Number(params.id), [params.id]);

  const { rows: centros } = useCentros();
  const centroNombre = useMemo(
    () => centros.find((c) => c.id === centroId)?.nombre ?? '…',
    [centros, centroId],
  );

  const { data: aulas = [], isLoading, error } = useAulasByCentro(centroId);

  const createAula = useCreateAula(centroId);
  const updateAula = useUpdateAula(centroId);
  const toggleAula = useToggleAula(centroId);
  const deleteAula = useDeleteAula(centroId);

  // helper consistente para activo que puede venir como boolean, 0/1 o "0"/"1"
  const isTrue = (v: unknown) => v === true || v === 1 || v === '1';

  const [editing, setEditing] = useState<{
    mode: 'new' | 'edit';
    aulaId?: number;
  } | null>(null);
  const [drawerAulaId, setDrawerAulaId] = useState<number | null>(null);

  // Switch Activas/Inactivas
  const [mostrarActivas, setMostrarActivas] = useState<boolean>(true);

  // Filtrado robusto por estado
  const aulasFiltradas = useMemo(
    () => aulas.filter((a) => isTrue(a.activo) === mostrarActivas),
    [aulas, mostrarActivas],
  );

  const currentAula = aulas.find((a) => a.id === editing?.aulaId);

  const normalizeForm = (data: AulaFormData): AulaFormData => ({
    ...data,
    internetObservaciones: data.internetObservaciones?.trim() || undefined,
  });

  async function handleCreate(data: AulaFormData) {
    await createAula.mutateAsync({ id_centro: centroId, ...normalizeForm(data) });
    setEditing(null);
  }

  async function handleUpdate(data: AulaFormData) {
    if (!currentAula) return;
    await updateAula.mutateAsync({ id: currentAula.id, ...normalizeForm(data) });
    setEditing(null);
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box p={2}>
        {!centroId && <Typography>Cargando centro…</Typography>}
        {isLoading && <Typography>Cargando aulas…</Typography>}
        {error && <Typography>Error cargando aulas</Typography>}

        <Typography variant="h2" gutterBottom>
          Aulas del centro {centroNombre}
        </Typography>

        {/* Botón + Switch filtro */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Button variant="contained" color="primary" onClick={() => setEditing({ mode: 'new' })}>
            Nueva aula
          </Button>

          <FormControlLabel
            control={
              <Switch
                checked={mostrarActivas}
                onChange={(e) => setMostrarActivas(e.target.checked)}
                color="primary"
                inputProps={{ 'aria-label': 'Conmutar entre Aulas activas e inactivas' }}
              />
            }
            label={mostrarActivas ? 'Mostrando: Activas' : 'Mostrando: Inactivas'}
          />
        </Stack>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Capacidad</TableCell>
              <TableCell>Prioridad</TableCell> {/* ← NUEVO */}
              <TableCell>Uso</TableCell>
              <TableCell>Internet</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aulasFiltradas.map((a) => {
              const isActiva = isTrue(a.activo); // ← usar helper robusto
              return (
                <TableRow key={a.id}>
                  <TableCell>{a.nombre}</TableCell>
                  <TableCell>
                    <AulaCapacityChip capacidad={a.capacidad} cat={a.capacidad_cat} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={a.prioridad ?? 'MEDIA'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={a.uso_aula ?? 'MIXTA'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={a.internet_perfil ?? 'BASICO'} />
                  </TableCell>
                  <TableCell>{isActiva ? 'Activa' : 'Inactiva'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setEditing({ mode: 'edit', aulaId: a.id })}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => toggleAula.mutate({ id: a.id, activo: isActiva })} // ← enviar booleano real
                      >
                        {isActiva ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setDrawerAulaId(a.id)}
                      >
                        Bloqueos
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => window.confirm('¿Eliminar aula?') && deleteAula.mutate(a.id)}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Estados vacíos contextuales */}
            {!aulasFiltradas.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  {aulas.length === 0
                    ? 'No hay aulas en este centro.'
                    : mostrarActivas
                      ? 'No hay aulas activas.'
                      : 'No hay aulas inactivas.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Modal para editar/crear aula */}
        <Modal open={!!editing} onClose={() => setEditing(null)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2,
              boxShadow: 24,
              minWidth: 300,
            }}
          >
            <Typography variant="h3" gutterBottom>
              {editing?.mode === 'new' ? 'Nueva aula' : `Editar aula ${currentAula?.nombre ?? ''}`}
            </Typography>
            <AulaForm
              defaultValues={
                editing?.mode === 'edit'
                  ? {
                      nombre: currentAula?.nombre,
                      capacidad: currentAula?.capacidad ?? 1,
                      capacidad_cat: (currentAula?.capacidad_cat ?? 'S') as 'S' | 'M' | 'L',
                      planta: currentAula?.planta ?? undefined,
                      zona: currentAula?.zona ?? undefined,
                      prioridad: (currentAula?.prioridad ?? 'MEDIA') as 'ALTA' | 'MEDIA' | 'BAJA', // ← NUEVO
                      usoAula: (currentAula?.uso_aula ?? 'MIXTA') as
                        | 'MIXTA'
                        | 'SOLO_ONLINE'
                        | 'SOLO_PRESENCIAL',
                      internetPerfil: (currentAula?.internet_perfil ?? 'BASICO') as
                        | 'SIN_INTERNET'
                        | 'BASICO'
                        | 'ESTABLE',
                      internetObservaciones: currentAula?.internet_observaciones ?? undefined,
                    }
                  : {
                      capacidad: 1,
                      capacidad_cat: 'S',
                      prioridad: 'MEDIA',
                      usoAula: 'MIXTA',
                      internetPerfil: 'BASICO',
                    }
              }
              onSubmit={editing?.mode === 'new' ? handleCreate : handleUpdate}
              submitText={editing?.mode === 'new' ? 'Crear' : 'Guardar cambios'}
            />
            <Box mt={2}>
              <Button variant="outlined" color="primary" onClick={() => setEditing(null)}>
                Cerrar
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Drawer de bloqueos */}
        {drawerAulaId != null && (
          <BloqueosDrawer aulaId={drawerAulaId} open={true} onClose={() => setDrawerAulaId(null)} />
        )}
      </Box>
    </ThemeProvider>
  );
}
